import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../lib/auth';

interface Props {
  children: ReactNode;
}

// Path consentiti allo staff: solo l'area dedicata /staff/* e nient'altro.
// Tutto il resto reindirizza a /staff così non vede sidebar o moduli che
// non lo riguardano.
function isStaffAllowedPath(path: string): boolean {
  // /timbra è raggiungibile anche dallo staff (QR del locale → /timbra?...).
  return path === '/staff' || path.startsWith('/staff/') || path === '/timbra';
}

/**
 * Avvolge le pagine che richiedono autenticazione.
 *
 * - Se /auth/me sta caricando → spinner minimal
 * - Se non loggato → redirect a /login
 * - Se loggato come `staff` e sta provando ad accedere a un path NON
 *   sotto /staff/* → redirect a /staff (il loro home)
 * - Altrimenti → renderizza i children
 */
export function ProtectedRoute({ children }: Props) {
  const { isLoading, isLoggedIn, me, talent } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-sm text-ink/60">Caricamento…</p>
      </div>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Guard talent-only: l'ex-dipendente senza membership attive vede SOLO il
  // proprio profilo bacheca e il feed delle posizioni. Il resto reindirizza.
  if (talent && !['/lavoro', '/profilo-bacheca', '/posizioni'].includes(location.pathname)) {
    return <Navigate to="/profilo-bacheca" replace />;
  }

  // Guard staff: blocca qualsiasi tentativo di navigare fuori da /staff/*.
  // Vale sia per click su link che per URL digitati a mano nell'address bar.
  if (me?.role === 'staff' && !isStaffAllowedPath(location.pathname)) {
    return <Navigate to="/staff" replace />;
  }

  return <>{children}</>;
}
